import PropTypes from 'prop-types';
import { Box, Card, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import {
  AcUnit,
  Air,
  Cloud,
  LocalFireDepartment,
  NetworkCheck,
  Opacity,
  Pool,
  PropaneTank,
  Radar,
  Videocam,
  Water,
  WbSunny,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Thermometer } from '../../../../../assets/icon-thermometer.svg';
import { ReactComponent as Energy } from '../../../../../assets/icon-energy.svg';
import DigitalSensorValue from '../../../../DigitalSensorValue';
import { useLocationsCacheContext } from '../../../../../context/LocationsCacheContext';
import ColorReading from '../../../../ColorReading';
import CustomVideoPlayer from '../../CustomVideoPlayer';
import PositioningDisplay from '../../../../Facilities/CenterPane/SensorReadings/components/SensorDisplay/PositioningDisplay';
import ActuatorControlPanel from '../../../../ActuatorControlPanel';
import OdometerDisplay from '../../../../Facilities/CenterPane/SensorReadings/components/SensorDisplay/OdometerDisplay';
import { COLORS } from '../../../../Facilities/CenterPane/SensorReadings/components/SensorDisplay/utils';
import { findItemWithId } from '../../../../../pages/Facilities/utils';
import PartDisplay from '../../../../Facilities/CenterPane/PartReadings/PartDisplay';
import { componentTypes } from '../ChartSelector/SensorSelector';

const FONTSIZE = '70px';

export const snapshotColorOptions = {
  green: 'rgba(20, 145, 83, 0.8)',
  orange: 'rgba(211, 78, 50, 0.8)',
  lightblue: 'rgba(99, 145, 201, 0.8)',
  darkblue: 'rgba(24, 43, 84, 0.8)',
  purple: 'rgba(124, 42, 134, 0.8)',
  grey: 'rgba(140, 140, 140, 0.8)',
};

export const widgetIcons = {
  temperature: (
    <Thermometer style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
  'water-level': (
    <Opacity style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
  pool: <Pool style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  energy: (
    <Energy
      style={{
        height: FONTSIZE,
        width: 'auto',
        color: 'white',
      }}
    />
  ),
  water: <Water style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  'air-conditioning': (
    <AcUnit style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
  cloud: <Cloud style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  sun: <WbSunny style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  air: <Air style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  'propane-tank': (
    <PropaneTank style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
  camera: (
    <Videocam style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
  radar: <Radar style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />,
  fire: (
    <LocalFireDepartment
      style={{ height: FONTSIZE, width: 'auto', color: 'white' }}
    />
  ),
  'internet-traffic': (
    <NetworkCheck style={{ height: FONTSIZE, width: 'auto', color: 'white' }} />
  ),
};

const isNumericalMeasuringMode = m => !['enum', 'color'].includes(m);

const renderActuator = (actuator, readOnly) => (
  <Card
    key={actuator.id}
    sx={{
      height: '100%',
      width: '100%',
      borderRadius: '16px',
    }}
  >
    <Box
      sx={{
        transform: 'scale(1.2)',
        transformOrigin: 'top left',
        width: '83.33%',
        height: '83.33%',
        '& *': { pointerEvents: readOnly ? 'auto' : 'none' },
      }}
    >
      <ActuatorControlPanel
        gateway={actuator.gateway}
        deviceId={actuator.id}
        deviceName={actuator.name}
        exposes={actuator.exposes}
        icon={actuator.icon}
        type="mini"
      />
    </Box>
  </Card>
);

const renderPart = part => (
  <Box
    key={part.id}
    sx={{
      '.MuiCard-root': { borderRadius: '16px' },
      transform: 'scale(1.2)',
      transformOrigin: 'top left',
      width: '83.33%',
      height: '83.33%',
    }}
  >
    <PartDisplay part={part} hideMoreOptions />
  </Box>
);

const renderSensor = (sensor, t, readOnly) => {
  const mode = sensor.measuring_mode;

  if (mode === 'positioning') {
    return (
      <Box key={sensor.id} sx={{ height: '100%', width: '100%' }}>
        <PositioningDisplay sensor={sensor} simplified={!readOnly} />
      </Box>
    );
  }

  if (mode === 'digital') {
    return (
      <Box key={sensor.id} fontSize="45px">
        <DigitalSensorValue
          binarySensor={sensor.threshold?.[sensor.reading]}
          noReading={sensor.reading == null}
          sensorId={sensor.id}
          colorized
          textBackground
        />
      </Box>
    );
  }

  if (mode === 'color') {
    return (
      <Box key={sensor.id}>
        <ColorReading value={sensor.reading} width="50px" height="50px" />
      </Box>
    );
  }

  if (mode === 'streaming') {
    return (
      <Box key={sensor.id} sx={{ height: '100%', width: '100%' }}>
        <CustomVideoPlayer id={sensor.id} credentials={sensor.threshold} />
      </Box>
    );
  }

  let read = sensor.reading;
  const isNum = isNumericalMeasuringMode(mode);

  if ((isNum && Number.isNaN(read)) || (!isNum && read == null)) {
    read = t('management_menu.display_management.view.no_reading');
  } else if (isNum) {
    read = (
      <Box
        sx={{
          fontWeight: 600,
          color: COLORS[sensor.status?.toUpperCase()] || 'white',
          WebkitTextFillColor: COLORS[sensor.status?.toUpperCase()] || 'white',
          WebkitTextStroke: '1px black',
          textShadow:
            '-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black',
          fontSize: '40px',
        }}
      >
        <OdometerDisplay sensor={sensor} />
      </Box>
    );
  }

  return (
    <Typography key={sensor.id} color="white" variant="h4">
      {read}
    </Typography>
  );
};

const Snapshot = ({ widget, height, width, partsData, readOnly }) => {
  const { sensors, actuators } = useLocationsCacheContext();
  const { t } = useTranslation();

  const hasExclusive = useMemo(
    () =>
      widget.items.map(i => {
        const sensor = findItemWithId(sensors, i.sensor);
        const actuator = findItemWithId(actuators, i.sensor);
        const part = findItemWithId(partsData, i.sensor);

        if (actuator) return componentTypes.ACTUATOR;
        if (part) return componentTypes.PART;
        if (['streaming', 'positioning'].includes(sensor?.measuring_mode))
          return componentTypes.SENSOR;
        return componentTypes.NOTHING;
      }),
    [actuators, partsData, sensors, widget.items],
  );

  const readingsDisplay = useMemo(() => {
    return widget.items.reduce((acc, curr) => {
      const sensor = findItemWithId(sensors, curr.sensor);
      const actuator = findItemWithId(actuators, curr.sensor);
      const part = findItemWithId(partsData, curr.sensor);

      if (!sensor && !actuator && !part) return acc;

      let val;
      if (actuator) {
        val = renderActuator(actuator, readOnly);
      } else if (part) {
        val = renderPart(part);
      } else {
        val = renderSensor(sensor, t, readOnly);
      }

      if (acc.length > 0) {
        acc.push(
          <Typography
            key={`divider-${curr.sensor}`}
            padding="0 5px"
            color="white"
            variant="h4"
          >
            /
          </Typography>,
        );
      }
      acc.push(val);
      return acc;
    }, []);
  }, [widget.items, sensors, actuators, partsData, readOnly, t]);

  const hasPart = hasExclusive.includes(componentTypes.PART);
  const hasActuator = hasExclusive.includes(componentTypes.ACTUATOR);
  const hasSensor = hasExclusive.includes(componentTypes.SENSOR);

  const isWide = width > 1 && height > 2;
  const isTall = width === 1 && height > 3;
  const isCompact = height < 3;

  const showIcon = !hasSensor && (isWide || isTall);
  const showInlineReadings = isCompact || hasSensor;
  const showBottomReadings = height > 2 && !hasSensor;

  const backgroundColor =
    snapshotColorOptions[widget.color] || snapshotColorOptions.grey;

  if (hasPart || hasActuator) return readingsDisplay;

  return (
    <Stack
      sx={{ backgroundColor, height: '100%', padding: '10px 15px' }}
      justifyContent="space-between"
      alignItems="flex-end"
    >
      <Stack spacing={1} width="100%" height="100%">
        <Typography color="white" variant="h6">
          {widget.title}
        </Typography>

        {showIcon && (
          <Stack
            spacing={1}
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {widgetIcons[widget.icon]}
          </Stack>
        )}

        {showInlineReadings && (
          <Stack
            flexDirection="row"
            alignItems="center"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            {readingsDisplay}
          </Stack>
        )}
      </Stack>

      {showBottomReadings && (
        <Stack flexDirection="row" alignItems="center" flexWrap="wrap">
          {readingsDisplay}
        </Stack>
      )}
    </Stack>
  );
};

Snapshot.propTypes = {
  widget: PropTypes.shape({
    color: PropTypes.any,
    icon: PropTypes.any,
    title: PropTypes.any,
    items: PropTypes.array,
  }).isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  partsData: PropTypes.array.isRequired,
  readOnly: PropTypes.bool.isRequired,
};

export default Snapshot;
